import styles from './Poll.module.css'
import clsx from 'clsx'
import { BasicStyledComponent, DSPoll } from 'shared-definitions/types'
import EditButton from 'shared-components/service/EditButton'
import { AppEventsContext } from '../contexts/AppEventsContext'
import { PICK_SOURCE_ATTR } from '../polls/pick-flight'

/** A choice's share of the votes, rounded to an integer, as drawn ("0" with no votes, never
 *  "NaN"). One formula for the bars and the post-vote ask's "Be the first of the N%". */
export function choicePercent(value: number, votes: number): string {
  return votes > 0 ? ((value / votes) * 100).toFixed(0) : '0'
}

interface PollProps extends BasicStyledComponent {
  poll: DSPoll
  votedId: null | string
  onSubmit: (itemId: string) => void
  choicesClassName?: string
  showVoteResults: boolean
  /** Under the choices: the change/remove hint or the Undo (`polls/PollFootnote`). */
  footer?: React.ReactNode
  /** The accessible name of a choice when a tap removes the vote (the reader's own, signed in). */
  choiceLabel?: (itemId: string, answer: string) => string | undefined
}

const Poll: React.VFC<PollProps> = ({
  showVoteResults,
  className,
  poll,
  onSubmit,
  votedId,
  choicesClassName,
  footer,
  choiceLabel,
}) => {
  const { appEvent } = AppEventsContext.useContainer()
  const resultsShown = votedId !== null && showVoteResults
  return (
    <>
      <EditButton pLink={poll.pLinkEdit} pLinkRequestUpdate={null} />
      <div
        className={clsx(className, {
          [styles.showResults]: votedId === null || !showVoteResults,
        })}
      >
        <h3 className={styles.title}>{poll.question}</h3>
        <div className={styles.votes}>{`${poll.votes} votes`}</div>
        <div className={clsx(styles.choicesGrid, choicesClassName)}>
          {poll.items.map(item => {
            const percent = choicePercent(item.value, poll.votes)
            return (
              <button
                type="button"
                key={item.id}
                aria-label={choiceLabel?.(item.id, item.answer)}
                className={clsx(styles.button, { [styles.buttonVoted]: votedId === item.id })}
                onClick={() => {
                  // Always: after a vote the poll store decides — signed in, another choice
                  // changes the vote and the same one removes it; signed out, any tap asks to
                  // sign in (polls in the conversation, 2026-10-24).
                  onSubmit(item.id)
                  appEvent({
                    event: 'widget-interaction',
                    label: item.answer,
                    widgetType: 'Poll',
                    url: window.location.href,
                  })
                }}
              >
                <div className={styles.choiceText}>
                  <span>{item.answer}</span>
                  {resultsShown && votedId === item.id ? (
                    // Under the label, never beside it (a long label would be squeezed). The pick
                    // flight's source (`polls/pick-flight`): it flies to the rail card's pick row.
                    <span className={styles.pickChip} {...{ [PICK_SOURCE_ATTR]: poll.id }}>
                      <svg
                        className={styles.pickChipCheck}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        aria-hidden="true"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {'Your pick'}
                    </span>
                  ) : null}
                </div>
                <div className={styles.choicePercent}>
                  {percent}
                  {'%'}
                </div>
                <div className={styles.progressHolder}>
                  <div className={styles.progress} style={{ width: `${percent}%` }} />
                </div>
              </button>
            )
          })}
        </div>
        {footer}
      </div>
    </>
  )
}

export default Poll
